<script setup lang="ts">
import { computed, watch } from 'vue'
import { actionLoadingText } from '@/shared/utils/actionLoadingText'
import { useRoute } from 'vue-router'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  type ButtonVariants
} from '@/shared/components/ui'
import { AlertTriangle, Info, CheckCircle } from 'lucide-vue-next'

interface Props {
  title?: string
  description?: string
  confirmText?: string
  cancelText?: string
  variant?: ButtonVariants['variant']
  loading?: boolean
  type?: 'danger' | 'info' | 'success' | 'warning'
  loadingText?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
}

const emit = defineEmits<{
  (e: 'confirm'): void
  (e: 'cancel'): void
}>()

const props = withDefaults(defineProps<Props>(), {
  title: 'Confirm Action',
  description: 'Are you sure you want to perform this action?',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  variant: 'default',
  loading: false,
  type: 'info',
  size: 'md'
})

const resolvedLoadingText = computed(
  () => props.loadingText ?? actionLoadingText(props.confirmText)
)

const sizeClasses = {
  sm: 'sm:max-w-[425px]',
  md: 'sm:max-w-[600px]',
  lg: 'sm:max-w-[800px]',
  xl: 'sm:max-w-[1000px]'
}

const isOpen = defineModel<boolean>('isOpen', { default: false })

const route = useRoute()
if (route) {
  watch(
    () => route.fullPath,
    () => {
      if (isOpen.value) {
        isOpen.value = false
      }
    }
  )
}

const handleConfirm = () => {
  emit('confirm')
}

const handleCancel = () => {
  isOpen.value = false
  emit('cancel')
}

/**
 * Icon + style mapping (clean extension for success support)
 */
const iconMap: Record<NonNullable<Props['type']>, { icon: unknown; class: string }> = {
  danger: {
    icon: AlertTriangle,
    class: 'bg-destructive/10 text-destructive'
  },
  info: {
    icon: Info,
    class: 'bg-info/10 text-info'
  },
  success: {
    icon: CheckCircle,
    class: 'bg-success/10 text-success'
  },
  warning: {
    icon: AlertTriangle,
    class: 'bg-warning/10 text-warning'
  }
}
</script>

<template>
  <Dialog :open="isOpen" @update:open="(val) => (isOpen = val)">
    <DialogContent
      overlay-class="backdrop-blur-sm"
      :class="[sizeClasses[size], 'overflow-hidden p-0 flex flex-col max-h-[85vh]']"
    >
      <!-- SCROLLABLE BODY -->
      <div class="p-6 overflow-y-auto flex-1">
        <div class="flex items-start gap-4">
          <div
            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
            :class="iconMap[type].class"
          >
            <component :is="iconMap[type].icon" class="h-5 w-5" />
          </div>

          <!-- CONTENT -->
          <div class="flex-1 space-y-1">
            <DialogHeader class="text-left gap-y-1">
              <DialogTitle class="text-lg font-semibold">
                {{ title }}
              </DialogTitle>

              <DialogDescription class="text-sm text-muted-foreground leading-normal">
                {{ description }}
              </DialogDescription>

              <div v-if="$slots.default" class="mt-4">
                <slot />
              </div>
            </DialogHeader>
          </div>
        </div>
      </div>

      <!-- FOOTER -->
      <DialogFooter
        class="bg-muted/50 px-6 py-4 flex flex-row items-center justify-end gap-2 shrink-0 border-t"
      >
        <Button variant="ghost" @click="handleCancel" :disabled="loading" class="px-5">
          {{ cancelText }}
        </Button>

        <Button
          :variant="
            type === 'danger'
              ? 'destructive'
              : type === 'success'
                ? 'success'
                : type === 'warning'
                  ? 'warning'
                  : variant
          "
          @click="handleConfirm"
          :loading="loading"
          class="px-5 text-white"
          :loading-text="resolvedLoadingText"
        >
          {{ confirmText }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
